import { FC, Fragment, useMemo, useState } from 'react';
import { Avatar, Button, Col, Divider, Dropdown, Flex, Input, Menu, Modal, Row, Space, Tag, Typography } from 'antd';
import {
    TbAdjustments,
    TbDotsVertical,
    TbInfoCircle,
    TbPlus,
    TbShieldPlus,
    TbSitemap,
    TbTrash,
    TbUsers,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import { teamService } from '@/services';
import {
    ActionsDropdownButton,
    ButtonTabs,
    CompactCollapsible,
    DropdownMenuPanel,
    EmptySection,
    HierarchyTreeRow,
    LearnMoreFooter,
    TeamsActionsDropdown,
    VirtualizedList,
    WorkspaceResourceStatusIndicators,
} from '@/shared';
import { PlatformForm as Form, PlatformSelect as Select } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useTeamStore } from '@/stores';
import {
    CollaboratorPermissionType,
    Subteam,
    Team,
    TeamResourceAccessPrincipalType,
    TeamResourceAccessResourceType,
    TeamVisibilityType,
} from '@/types';
import { formatDate } from '@/utilities';

import CreateSubteamModal from '../../../../../components/CreateSubteamModal';
import { loadResources, permissionOptions, resourceTypeOptions, WorkspaceResourceOption } from './configuration';

interface TeamListItemProps {
    team: Team;
}

const TeamListItem: FC<TeamListItemProps> = ({ team }): JSX.Element => {
    const [expanded, setExpanded] = useState(false);
    const [subteamModalOpen, setSubteamModalOpen] = useState(false);
    const [teamModalOpen, setTeamModalOpen] = useState(false);
    const [grantModalOpen, setGrantModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [settingsName, setSettingsName] = useState(team.name);
    const [settingsVisibility, setSettingsVisibility] = useState<TeamVisibilityType>(
        team.visibility ?? TeamVisibilityType.PRIVATE,
    );
    const [resourceOptions, setResourceOptions] = useState<WorkspaceResourceOption[]>([]);
    const [resourcesLoading, setResourcesLoading] = useState(false);
    const [grantPrincipal, setGrantPrincipal] = useState<{
        type: TeamResourceAccessPrincipalType;
        subteam?: Subteam;
    }>({ type: TeamResourceAccessPrincipalType.TEAM });
    const [grantForm] = Form.useForm();

    const { fetchTeams, joinTeam, updateTeam } = useTeamStore();

    const { token } = useAntDesignTheme();
    const subteams = team?.subteams ?? [];
    const { getUsageState } = usePlanUsageLimits();
    const subteamUsage = getUsageState('subteamsPerTeam', team?.subteamCount ?? subteams.length);

    const accessByResourceId = useMemo(() => {
        return new Map((team.resourceAccess ?? []).map((access) => [access.resourceId, access]));
    }, [team.resourceAccess]);

    const refreshResources = async (type: TeamResourceAccessResourceType) => {
        setResourcesLoading(true);

        try {
            setResourceOptions(await loadResources(type));
        } finally {
            setResourcesLoading(false);
        }
    };

    const openGrantModal = async (principalType: TeamResourceAccessPrincipalType, subteam?: Subteam) => {
        const resourceType = TeamResourceAccessResourceType.INVESTMENT;

        setGrantPrincipal({ type: principalType, subteam });
        grantForm.setFieldsValue({
            resourceType,
            permission: CollaboratorPermissionType.VIEW,
        });
        setGrantModalOpen(true);
        await refreshResources(resourceType);
    };

    const handleGrantResourceAccess = async () => {
        const values = await grantForm.validateFields();

        setSubmitting(true);

        try {
            if (grantPrincipal.type === TeamResourceAccessPrincipalType.SUBTEAM && grantPrincipal.subteam?.id) {
                await teamService.grantSubteamResourceAccess(team.id, grantPrincipal.subteam.id, values);
            } else {
                await teamService.grantResourceAccess(team.id, values);
            }

            toast('Resource Access Granted', {
                description:
                    grantPrincipal.type === TeamResourceAccessPrincipalType.SUBTEAM
                        ? grantPrincipal.subteam?.name
                        : team.name,
            });
            setGrantModalOpen(false);
            grantForm.resetFields();
            await fetchTeams();
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteSubteam = async (subteam: Subteam) => {
        Modal.confirm({
            title: 'Delete Subteam',
            content: `Delete ${subteam.name}? Team members will lose access granted through this subteam.`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                await teamService.deleteSubteam(team.id, subteam.id);
                await fetchTeams();
            },
        });
    };

    const handleJoinTeam = async () => {
        setSubmitting(true);

        try {
            await joinTeam(team.id);
            toast('Team Joined', { description: team.name });
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateTeamSettings = async () => {
        setSubmitting(true);

        try {
            await updateTeam(team.id, {
                name: settingsName,
                visibility: settingsVisibility,
            });
            toast('Team Updated', { description: settingsName });
            await fetchTeams();
            setTeamModalOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseTeamModal = () => {
        setSettingsName(team.name);
        setSettingsVisibility(team.visibility ?? TeamVisibilityType.PRIVATE);
        setTeamModalOpen(false);
    };

    const subteamMenu = (subteam: Subteam) => (
        <DropdownMenuPanel>
            <Menu.Item
                key='grant-resource'
                icon={<TbShieldPlus size='1rem' />}
                onClick={() => openGrantModal(TeamResourceAccessPrincipalType.SUBTEAM, subteam)}
            >
                Grant Resource Access
            </Menu.Item>
            <Menu.Item key='delete' icon={<TbTrash size='1rem' />} danger onClick={() => handleDeleteSubteam(subteam)}>
                Delete Subteam
            </Menu.Item>
        </DropdownMenuPanel>
    );

    return (
        <Fragment>
            <CompactCollapsible
                contentStyle={{ padding: '0.75rem 0 0.75rem 3.5rem' }}
                headerExtra={
                    <Space size='middle'>
                        <span style={{ color: token.colorTextTertiary }}>
                            {team?.updatedAt === team?.createdAt ? 'Created' : 'Edited'}{' '}
                            {team?.updatedAt ? formatDate(team?.updatedAt, 'round') : undefined}
                        </span>
                        {team.canJoin ? (
                            <Button type='primary' loading={submitting} disabled={submitting} onClick={handleJoinTeam}>
                                Join
                            </Button>
                        ) : undefined}
                        <TeamsActionsDropdown team={team} children={<ActionsDropdownButton />} />
                    </Space>
                }
                headerStyle={{ paddingBlock: '0.75rem', paddingInline: 0 }}
                icon={
                    <Avatar
                        icon={
                            <TbUsers
                                stroke={token.colorTextDisabled}
                                fill={token.colorFillAlter}
                                size='1.5rem'
                                style={{ verticalAlign: '-0.125rem' }}
                            />
                        }
                        shape='square'
                        size='large'
                        style={{
                            backgroundColor: token.colorBgBase,
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    />
                }
                label={
                    <div>
                        <div>
                            <Button
                                onClick={() => setTeamModalOpen(true)}
                                type='text'
                                style={{ color: token.colorText, fontWeight: 500, height: 'auto', paddingBlock: 0 }}
                            >
                                {team?.name}
                            </Button>
                            <WorkspaceResourceStatusIndicators
                                archived={team?.archived}
                                isNew={team?.createdAt === team?.updatedAt}
                                locked={team?.locked}
                            />
                        </div>
                        <small style={{ color: token.colorTextTertiary }}>
                            {team?.visibility} · {team?.teammateCount ?? team?.teammates?.length ?? 0} members ·{' '}
                            {team?.subteamCount ?? subteams.length} subteams
                        </small>
                    </div>
                }
                open={expanded}
                sectionStyle={{
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    borderTop: 0,
                }}
                onOpenChange={setExpanded}
            >
                {subteams.length ? (
                    <div>
                        {subteams.map((subteam, index) => (
                            <HierarchyTreeRow
                                continueToSiblings={index < subteams.length - 1}
                                depth={1}
                                firstSibling={index === 0}
                                key={subteam.id}
                            >
                                <Row
                                    align='middle'
                                    wrap={false}
                                    style={{
                                        borderTop: `1px solid ${token.colorBorderSecondary}`,
                                        padding: '0.625rem 0',
                                    }}
                                >
                                    <Col flex='none'>
                                        <Avatar
                                            size='small'
                                            shape='square'
                                            icon={<TbSitemap size='0.875rem' />}
                                            style={{
                                                backgroundColor: token.colorBgContainer,
                                                border: `1px solid ${token.colorBorder}`,
                                                color: token.colorTextTertiary,
                                                marginRight: '0.5rem',
                                            }}
                                        />
                                    </Col>
                                    <Col flex='auto'>
                                        <div>{subteam.name}</div>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            {subteam.members?.length ?? 0} members ·{' '}
                                            {subteam.resourceAccess?.length ?? 0} resource grants
                                        </small>
                                    </Col>
                                    <Col flex='none'>
                                        <Dropdown popupRender={() => subteamMenu(subteam)} trigger={['click']}>
                                            <Button type='text' icon={<TbDotsVertical size='1rem' />} />
                                        </Dropdown>
                                    </Col>
                                </Row>
                            </HierarchyTreeRow>
                        ))}
                    </div>
                ) : (
                    <EmptySection
                        height={16 * 10}
                        icon={<TbSitemap size='1.25rem' />}
                        label='No Subteams'
                        description='Subteams created for this team will appear here.'
                        extra={
                            <Button
                                disabled={subteamUsage.reached}
                                icon={<TbPlus size='0.875rem' />}
                                size='small'
                                title={subteamUsage.reached ? subteamUsage.reason : undefined}
                                type='primary'
                                onClick={() => setSubteamModalOpen(true)}
                            >
                                New Subteam
                            </Button>
                        }
                    />
                )}
            </CompactCollapsible>

            <Modal
                title={team.name}
                open={teamModalOpen}
                onCancel={handleCloseTeamModal}
                footer={
                    <Flex align='center' justify='space-between'>
                        <LearnMoreFooter align='left' topic='Teams and workspace permissions' />
                        <Flex gap='0.5rem'>
                            <Button disabled={submitting} onClick={handleCloseTeamModal}>
                                Cancel
                            </Button>
                            <Button
                                disabled={!settingsName.trim()}
                                loading={submitting}
                                onClick={() => void handleUpdateTeamSettings()}
                                type='primary'
                            >
                                Save Changes
                            </Button>
                        </Flex>
                    </Flex>
                }
                styles={{
                    body: { minHeight: '25rem', padding: 0 },
                    footer: {
                        borderTop: `1px solid ${token.colorBorder}`,
                        marginTop: 0,
                        paddingTop: '1rem',
                    },
                }}
                width={840}
                destroyOnClose
            >
                <ButtonTabs
                    ariaLabel='Team details'
                    style={{ minHeight: '25rem' }}
                    items={[
                        {
                            key: 'overview',
                            label: 'Overview',
                            icon: <TbInfoCircle size='0.875rem' />,
                            children: (
                                <div style={{ padding: '1rem 1.5rem' }}>
                                    <Typography.Paragraph type='secondary'>{team.description}</Typography.Paragraph>
                                    <Row gutter={[16, 16]}>
                                        <Col span={8}>
                                            <Typography.Text type='secondary'>Visibility</Typography.Text>
                                            <div>{team.visibility}</div>
                                        </Col>
                                        <Col span={8}>
                                            <Typography.Text type='secondary'>Members</Typography.Text>
                                            <div>{team.teammateCount ?? team.teammates?.length ?? 0}</div>
                                        </Col>
                                        <Col span={8}>
                                            <Typography.Text type='secondary'>Subteams</Typography.Text>
                                            <div>{team.subteamCount ?? subteams.length}</div>
                                        </Col>
                                    </Row>
                                </div>
                            ),
                        },
                        {
                            key: 'members',
                            label: 'Members',
                            icon: <TbUsers size='0.875rem' />,
                            children: team.teammates?.length ? (
                                <VirtualizedList
                                    estimateSize={49}
                                    getItemKey={(teammate) => teammate.id}
                                    height='100%'
                                    items={team.teammates}
                                    renderItem={(teammate) => (
                                        <Row
                                            align='middle'
                                            style={{
                                                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                                padding: '0.75rem 1.5rem',
                                            }}
                                        >
                                            <Col flex='auto'>
                                                {[
                                                    teammate.member?.user?.firstName ?? teammate.user?.firstName,
                                                    teammate.member?.user?.lastName ?? teammate.user?.lastName,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ') ||
                                                    teammate.member?.user?.emailAddress ||
                                                    teammate.user?.emailAddress ||
                                                    teammate.id}
                                            </Col>
                                            <Col flex='none'>
                                                <Tag>{teammate.permission}</Tag>
                                            </Col>
                                        </Row>
                                    )}
                                />
                            ) : (
                                <EmptySection
                                    height='100%'
                                    icon={<TbUsers size='1.25rem' />}
                                    label='No Members'
                                    description='Members assigned to this team will appear here.'
                                />
                            ),
                        },
                        {
                            key: 'subteams',
                            label: 'Subteams',
                            icon: <TbSitemap size='0.875rem' />,
                            children: (
                                <div style={{ padding: '1rem 1.5rem' }}>
                                    <Row align='middle' style={{ marginBottom: '0.75rem' }}>
                                        <Col flex='auto' />
                                        <Col flex='none'>
                                            <Button
                                                disabled={subteamUsage.reached}
                                                title={subteamUsage.reached ? subteamUsage.reason : undefined}
                                                type='primary'
                                                icon={<TbPlus size='0.875rem' />}
                                                onClick={() => setSubteamModalOpen(true)}
                                            >
                                                New Subteam
                                            </Button>
                                        </Col>
                                    </Row>
                                    {subteams.length ? (
                                        subteams.map((subteam, index) => (
                                            <HierarchyTreeRow
                                                continueToSiblings={index < subteams.length - 1}
                                                depth={1}
                                                firstSibling={index === 0}
                                                key={subteam.id}
                                            >
                                                <Row
                                                    align='middle'
                                                    style={{
                                                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                                        padding: '0.75rem 0',
                                                    }}
                                                >
                                                    <Col flex='auto'>
                                                        <div>{subteam.name}</div>
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            {subteam.members?.length ?? 0} members ·{' '}
                                                            {subteam.resourceAccess?.length ?? 0} resource grants
                                                        </small>
                                                    </Col>
                                                    <Col flex='none'>
                                                        <Dropdown
                                                            popupRender={() => subteamMenu(subteam)}
                                                            trigger={['click']}
                                                        >
                                                            <Button type='text' icon={<TbDotsVertical size='1rem' />} />
                                                        </Dropdown>
                                                    </Col>
                                                </Row>
                                            </HierarchyTreeRow>
                                        ))
                                    ) : (
                                        <EmptySection
                                            height={16 * 10}
                                            icon={<TbSitemap size='1.25rem' />}
                                            label='No Subteams'
                                            description='Subteams created for this team will appear here.'
                                        />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: 'access',
                            label: 'Access',
                            icon: <TbShieldPlus size='0.875rem' />,
                            children: (
                                <div style={{ padding: '1rem 1.5rem' }}>
                                    <Button
                                        icon={<TbShieldPlus size='0.875rem' />}
                                        onClick={() => openGrantModal(TeamResourceAccessPrincipalType.TEAM)}
                                    >
                                        Grant Resource
                                    </Button>
                                    <Divider />
                                    {(team.resourceAccess ?? []).map((access) => (
                                        <Row
                                            key={access.id}
                                            align='middle'
                                            style={{
                                                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                                padding: '0.75rem 0',
                                            }}
                                        >
                                            <Col flex='auto'>
                                                {access.resourceType} · {access.resourceId}
                                            </Col>
                                            <Col flex='none'>
                                                <Tag>{access.permission}</Tag>
                                            </Col>
                                        </Row>
                                    ))}
                                    {!team.resourceAccess?.length && (
                                        <EmptySection
                                            height={16 * 10}
                                            icon={<TbShieldPlus size='1.25rem' />}
                                            label='No Resource Grants'
                                            description='Granted workspace resource access will appear here.'
                                        />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: 'settings',
                            label: 'Settings',
                            icon: <TbAdjustments size='0.875rem' />,
                            children: (
                                <Form layout='vertical' style={{ padding: '1rem 1.5rem' }}>
                                    <Form.Item label='Name'>
                                        <Input
                                            value={settingsName}
                                            onChange={(event) => setSettingsName(event.target.value)}
                                        />
                                    </Form.Item>
                                    <Form.Item label='Visibility'>
                                        <Select
                                            value={settingsVisibility}
                                            onChange={setSettingsVisibility}
                                            options={[
                                                { label: 'Private', value: TeamVisibilityType.PRIVATE },
                                                { label: 'Workspace', value: TeamVisibilityType.WORKSPACE },
                                            ]}
                                        />
                                    </Form.Item>
                                </Form>
                            ),
                        },
                    ]}
                />
            </Modal>

            <CreateSubteamModal open={subteamModalOpen} team={team} onClose={() => setSubteamModalOpen(false)} />

            <Modal
                title={
                    grantPrincipal.type === TeamResourceAccessPrincipalType.SUBTEAM
                        ? `Grant Resource to ${grantPrincipal.subteam?.name}`
                        : `Grant Resource to ${team.name}`
                }
                open={grantModalOpen}
                okText='Grant Access'
                confirmLoading={submitting}
                onOk={handleGrantResourceAccess}
                onCancel={() => setGrantModalOpen(false)}
                destroyOnClose
            >
                <Form
                    form={grantForm}
                    layout='vertical'
                    onValuesChange={(changed) => {
                        if (changed.resourceType) {
                            grantForm.setFieldValue('resourceId', undefined);
                            void refreshResources(changed.resourceType);
                        }
                    }}
                >
                    <Form.Item
                        name='resourceType'
                        label='Resource Type'
                        rules={[{ required: true, message: 'Select a resource type' }]}
                    >
                        <Select options={resourceTypeOptions.map((type) => ({ label: type, value: type }))} />
                    </Form.Item>
                    <Form.Item
                        name='resourceId'
                        label='Resource'
                        rules={[{ required: true, message: 'Select a resource' }]}
                    >
                        <Select
                            showSearch
                            loading={resourcesLoading}
                            optionFilterProp='label'
                            options={resourceOptions.map((resource) => ({
                                label: resource.label,
                                value: resource.id,
                                disabled: accessByResourceId.has(resource.id),
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name='permission'
                        label='Permission'
                        rules={[{ required: true, message: 'Select a permission' }]}
                    >
                        <Select
                            options={permissionOptions.map((permission) => ({ label: permission, value: permission }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Fragment>
    );
};

export default TeamListItem;
